// React hook wrapper around `reduceCombatEngagements`. Uses a ref to thread
// `prevState` and the last-seen event id through memoized reducer runs so
// moraleDelta survives across renders without re-scanning from event 0.

import { useMemo, useRef } from 'react';
import {
  type CombatEngagementOptions,
  DEFAULT_COMBAT_OPTIONS,
  type EngagementVisualState,
  type SimEvent,
  reduceCombatEngagements,
} from './useCombatEngagements.js';

export function useCombatEngagements(
  events: ReadonlyArray<SimEvent> | undefined,
  currentTick: number,
  options: CombatEngagementOptions = DEFAULT_COMBAT_OPTIONS,
): ReadonlyMap<string, EngagementVisualState> {
  const list = events ?? [];
  const cursorRef = useRef<{
    lastId: number;
    state: Map<string, EngagementVisualState>;
  }>({ lastId: -1, state: new Map() });

  return useMemo(() => {
    const next = reduceCombatEngagements(
      list,
      cursorRef.current.lastId,
      cursorRef.current.state,
      currentTick,
      options,
    );
    const lastId = list.length > 0 ? (list[list.length - 1]!.id ?? -1) : -1;
    cursorRef.current = { lastId, state: next };
    return next;
  }, [list, currentTick, options]);
}
